/**
 * Node de verificação de tipo de mensagem
 * Ramifica o fluxo baseado na origem da mensagem (comentário ou DM)
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo componente para fluxos Instagram
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { MessageSquareMore, MessageCircle, AtSign } from 'lucide-react';
import { FlowNodeData } from '@/types/flows';
import { useMemo } from 'react';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Labels para os tipos de saída
 */
const outputLabels = {
  comment: { label: 'Comentário', icon: AtSign, color: 'text-pink-600 dark:text-pink-400' },
  direct_message: { label: 'DM (Direct)', icon: MessageCircle, color: 'text-purple-600 dark:text-purple-400' },
  default: { label: 'Padrão', icon: MessageSquareMore, color: 'text-gray-600 dark:text-gray-400' },
};

/**
 * Componente visual do node de verificação de tipo de mensagem
 */
export function MessageTypeCheckNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Obtém informações das edges conectadas via data injetado
  const connectedHandles = useMemo(() => {
    const edges = (data as any).edges || [];
    return new Set(edges.map((e: any) => e.sourceHandle));
  }, [data]);

  const outputs = Object.entries(outputLabels);

  return (
    <div 
      className={`bg-card rounded-lg shadow-lg border-2 min-w-[240px] transition-all ${
        selected 
          ? 'border-pink-500 ring-2 ring-pink-500/30' 
          : 'border-transparent'
      }`}
      style={{ 
        borderImage: selected ? undefined : 'linear-gradient(135deg, #833AB4, #E1306C, #F77737) 1',
        borderImageSlice: selected ? undefined : 1,
      }}
    >
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      {/* Handle de entrada no topo */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3"
        style={{ background: 'linear-gradient(135deg, #833AB4, #E1306C)' }}
      />
      
      {/* Cabeçalho do node com gradiente Instagram */}
      <div 
        className="px-3 py-2 rounded-t-lg flex items-center gap-2"
        style={{ background: 'linear-gradient(135deg, rgba(131, 58, 180, 0.15), rgba(225, 48, 108, 0.15), rgba(247, 119, 55, 0.15))' }}
      >
        <div 
          className="p-1.5 rounded"
          style={{ background: 'linear-gradient(135deg, #833AB4, #E1306C, #F77737)' }}
        >
          <MessageSquareMore className="h-4 w-4 text-white" />
        </div>
        <span className="font-semibold text-sm text-foreground">{(data as any).config?.nickname || 'Verificar Tipo Mensagem'}</span>
      </div>
      
      {/* Corpo com saídas */}
      <div className="p-2 space-y-2">
        {outputs.map(([handleId, config]) => {
          const isConnected = connectedHandles.has(handleId);
          const Icon = config.icon;
          
          return (
            <div key={handleId} className="relative">
              <div className="text-xs px-2 py-1.5 rounded border bg-muted/50 border-border flex items-center gap-2">
                <Icon className={`h-3 w-3 ${config.color}`} />
                <span className="font-medium">{config.label}</span>
              </div>
              
              {/* Handle de saída */}
              <Handle
                type="source"
                position={Position.Right}
                id={handleId}
                className={`!w-3 !h-3 transition-all ${
                  isConnected 
                    ? '!bg-pink-500' 
                    : '!bg-red-500 animate-pulse'
                }`}
                style={{ top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          );
        })}
        
        {/* Informação */}
        <p className="text-[10px] text-muted-foreground px-2 mt-2">
          Ramifica baseado na origem da mensagem
        </p>
      </div>
    </div>
  );
}
